import type { PackValidation, PackValidationIssue } from '../../../lib/api/operatorConsole';
import { Pill } from '../../../components/ui/Pill';
import { formatMoney, formatPercent, formatRange } from '../packBuilderFormat';

interface Props {
  validation: PackValidation | null;
  loading: boolean;
  currency: string | null;
  title?: string | undefined;
}

const H3 = 'mb-1 text-[11px] font-semibold uppercase tracking-wide text-[var(--co-muted)]';
/** An empty bucket is a blocking condition (EMPTY_BUCKETS, DESIGN §6), so its
 *  bar keeps a visible stub instead of collapsing to an invisible 0%. */
const EMPTY_BAR_WIDTH = 4;

function issueKey(issue: PackValidationIssue, index: number): string {
  return `${issue.code}-${index}`;
}

/** An issue names every card it found — HEAVY_CARD or CARDS_OUT_OF_BUCKETS can
 *  hand back hundreds of ids, and the whole list would bury the message it
 *  belongs to. The head is enough to go look; the count carries the rest. */
const MAX_SHOWN_IDS = 10;

function IssueIds({ ids }: { ids: number[] }) {
  if (ids.length === 0) return null;
  const shown = ids.slice(0, MAX_SHOWN_IDS);
  const rest = ids.length - shown.length;
  return (
    <span className="text-[var(--co-muted)]">
      {` (#${shown.join(', #')}${rest > 0 ? ` +${rest} more` : ''})`}
    </span>
  );
}

/**
 * Right column of the Items screen and the summary on Publish: what the
 * validator found (DESIGN §6) — the bucket spread and the issues it raised.
 * The panel computes nothing except the bar widths.
 */
export function PackHealthPanel({ validation, loading, currency, title = 'Pack health' }: Props) {
  const buckets = validation?.buckets ?? [];
  const chances = validation?.odds?.display_chances ?? {};
  const maxCount = Math.max(1, ...buckets.map((b) => b.count));

  return (
    <aside
      aria-label={title}
      className="sticky top-4 space-y-4 self-start rounded-xl border border-[var(--co-border)] bg-[var(--co-surface)] p-3"
    >
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-[13px] font-semibold text-[var(--co-text)]">{title}</h2>
        {loading ? (
          <span className="text-[11px] text-[var(--co-muted)]">Checking…</span>
        ) : validation ? (
          <Pill tone={validation.ok ? 'success' : 'error'}>
            {validation.ok ? 'Ready to publish' : `${validation.errors.length} blocking`}
          </Pill>
        ) : null}
      </div>

      {!validation ? (
        <p className="text-[12px] text-[var(--co-muted)]">
          {loading ? 'Running the validator…' : 'No validation yet.'}
        </p>
      ) : (
        <>
          <section>
            <h3 className={H3}>Buckets</h3>
            <ul aria-label="Buckets" className="space-y-1.5">
              {buckets.map((bucket) => {
                const empty = bucket.count === 0;
                return (
                  <li key={bucket.range} className="text-[11px]">
                    <div className="flex justify-between gap-2 font-mono">
                      <span className="text-[var(--co-text)]">{formatRange(bucket.range)}</span>
                      <span className="text-[var(--co-muted)]">
                        {bucket.count} · avg {formatMoney(bucket.avg_fmv, currency)} ·{' '}
                        {formatPercent(chances[bucket.range] ?? null, 1)}
                      </span>
                    </div>
                    <div className="h-1.5 w-full rounded bg-[var(--co-bg)]">
                      <div
                        className="h-1.5 rounded"
                        title={empty ? '0 cards — empty bucket' : `${bucket.count} cards`}
                        style={{
                          width: `${empty ? EMPTY_BAR_WIDTH : Math.round((bucket.count / maxCount) * 100)}%`,
                          backgroundColor: empty
                            ? 'var(--co-error)'
                            : bucket.side === 'low'
                              ? 'var(--co-muted)'
                              : 'var(--co-brand)',
                        }}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>

          <section className="space-y-2">
            {validation.errors.length > 0 && (
              <div>
                <h3 className={`${H3} text-[var(--co-error)]`}>Blocking</h3>
                <ul aria-label="Errors" className="space-y-1">
                  {validation.errors.map((issue, index) => (
                    <li key={issueKey(issue, index)} className="text-[12px]">
                      <Pill tone="error">{issue.code}</Pill> <span>{issue.message}</span>
                      {issue.ids ? <IssueIds ids={issue.ids} /> : null}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {validation.warnings.length > 0 && (
              <div>
                <h3 className={H3}>Warnings</h3>
                <ul aria-label="Warnings" className="space-y-1">
                  {validation.warnings.map((issue, index) => (
                    <li key={issueKey(issue, index)} className="text-[12px]">
                      <Pill tone={issue.level === 'info' ? 'info' : 'warning'}>{issue.code}</Pill>{' '}
                      <span>{issue.message}</span>
                      {issue.ids ? <IssueIds ids={issue.ids} /> : null}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {validation.errors.length === 0 && validation.warnings.length === 0 && (
              <p className="text-[12px] text-[var(--co-muted)]">No issues.</p>
            )}
          </section>
        </>
      )}
    </aside>
  );
}
